import { useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useCustomers } from "@/hooks/use-customers";

const MAX_VISIBLE = 50;

interface Props {
  value: string[];
  onChange: (ids: string[]) => void;
}

/** Søkbar avkrysningsliste over kunder. Viser de 50 første treffene; valgte kunder står alltid øverst. */
export default function CustomerMultiSelect({ value, onChange }: Props) {
  const { data: customers = [], isLoading } = useCustomers();
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("nb-NO");
    const selected = customers.filter((c) => value.includes(c.id));
    const rest = customers.filter((c) => !value.includes(c.id) && (!q || c.name.toLocaleLowerCase("nb-NO").includes(q)));
    return [...selected, ...rest].slice(0, MAX_VISIBLE);
  }, [customers, value, query]);

  const toggle = (id: string, checked: boolean) =>
    onChange(checked ? [...value, id] : value.filter((v) => v !== id));

  return (
    <div className="space-y-2">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Søk etter kunde"
        aria-label="Søk etter kunde"
        className="h-8"
      />
      <ul className="max-h-44 space-y-1 overflow-y-auto rounded border border-border p-2">
        {isLoading && <li className="text-sm text-muted-foreground">Laster kunder …</li>}
        {!isLoading && visible.length === 0 && <li className="text-sm text-muted-foreground">Ingen kunder funnet</li>}
        {visible.map((c) => (
          <li key={c.id} className="flex items-center gap-2 text-sm">
            <Checkbox
              id={`agent-customer-${c.id}`}
              checked={value.includes(c.id)}
              onCheckedChange={(checked) => toggle(c.id, checked === true)}
              aria-label={c.name}
            />
            <label htmlFor={`agent-customer-${c.id}`} className="cursor-pointer">{c.name}</label>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">{value.length} valgt</p>
    </div>
  );
}
